import { useEffect, useState } from 'react';
import { Button, MessageBar, MessageBarActions, MessageBarBody } from '@fluentui/react-components';
import type { SharedRunSummary } from '@qcbot/shared';
import { fetchSharedRuns, isTerminalPhase, type ReviewProgress } from '../api';
import { getCurrentFileName, getSourceDocumentKey } from '../officeApi';

const MAX_LISTED = 3;

/** Offers to join runs someone invited this user to — the one for the open file when it matches,
 * otherwise any run shared with them, since file names/URLs can differ between desktop and web. */
export function JoinBanner({ onJoin }: { onJoin: (runId: string) => void }) {
  const [runs, setRuns] = useState<SharedRunSummary[]>([]);
  const [matchesOpenFile, setMatchesOpenFile] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const matching = await fetchSharedRuns(getCurrentFileName(), await getSourceDocumentKey());
        const found = matching.length > 0 ? matching : await fetchSharedRuns();
        if (cancelled) return;
        setMatchesOpenFile(matching.length > 0);
        setRuns(found);
      } catch {
        // No banner is better than a blocking error: starting a run still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (runs.length === 0 || dismissed) return null;

  const describe = (run: SharedRunSummary) => {
    const finished = isTerminalPhase(run.phase as ReviewProgress['phase']);
    const status = finished ? `${run.openIssueCount} of ${run.issueCount} issues still open.` : 'Still running.';
    return `${run.createdByName ?? 'Someone'} ran QC${matchesOpenFile ? ' on this file' : ` on ${run.fileName}`}. ${status}`;
  };

  const listed = matchesOpenFile ? runs.slice(0, 1) : runs.slice(0, MAX_LISTED);
  return (
    <MessageBar intent="info" layout="multiline" style={{ marginBottom: 12 }}>
      <MessageBarBody>
        {matchesOpenFile ? (
          describe(listed[0])
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div>Runs shared with you</div>
            {listed.map((run) => (
              <div key={run.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12 }}>{describe(run)}</span>
                <Button size="small" appearance="primary" onClick={() => onJoin(run.id)}>
                  Join
                </Button>
              </div>
            ))}
          </div>
        )}
      </MessageBarBody>
      <MessageBarActions>
        {matchesOpenFile && (
          <Button size="small" appearance="primary" onClick={() => onJoin(listed[0].id)}>
            Join
          </Button>
        )}
        <Button size="small" appearance="secondary" onClick={() => setDismissed(true)}>
          {matchesOpenFile ? 'Start my own' : 'Dismiss'}
        </Button>
      </MessageBarActions>
    </MessageBar>
  );
}
